import { useCallback, useEffect, useReducer } from 'react';

import {
  HeaderSupportCodeQuery,
  useHeaderSupportCodeQuery,
} from '../../generated';

export interface CurrentSupportCode {
  status: 'current';
  code: string;
}

export interface RefreshingSupportCode {
  status: 'refreshing';
}

/** No code that is still current: it passed its expiresAt, or none loaded. */
export interface ExpiredSupportCode {
  status: 'expired';
}

export type SupportCodeState =
  | CurrentSupportCode
  | RefreshingSupportCode
  | ExpiredSupportCode;

export interface GetCurrentSupportCodeResult {
  code: string | undefined;
}

export interface UseSupportCodeResult {
  supportCode: SupportCodeState;
  getCurrentCode: () => Promise<GetCurrentSupportCodeResult>;
}

const supportCodeFrom = (data: HeaderSupportCodeQuery | undefined) => {
  const result = data?.supportCodeGet;
  return result?.__typename === 'SupportCode' ? result : undefined;
};

const isCurrent = (expiresAt: string) => Date.now() < Date.parse(expiresAt);

/**
 * The support code in effect now, read from authService. A code past its
 * expiresAt is never handed out, grace period or not. Every interaction goes
 * through `getCurrentCode`, which fetches the code again only once the one
 * held has expired. A failed fetch leaves the expired code in Apollo's last
 * result, so the state stays expired.
 */
export const useSupportCode = (): UseSupportCodeResult => {
  const { data, loading, refetch } = useHeaderSupportCodeQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const [, rerender] = useReducer((renders: number) => renders + 1, 0);

  const held = supportCodeFrom(data);
  const expiresAt = held?.expiresAt;

  // Re-renders when the held code expires, so it stops being shown. Fetching
  // the next one waits for an interaction.
  useEffect(() => {
    if (!expiresAt || !isCurrent(expiresAt)) {
      return undefined;
    }

    const timeoutId = setTimeout(rerender, Date.parse(expiresAt) - Date.now());

    return () => {
      clearTimeout(timeoutId);
    };
  }, [expiresAt]);

  const getCurrentCode = useCallback(async () => {
    if (held && isCurrent(held.expiresAt)) {
      return { code: held.code };
    }

    try {
      const refreshed = supportCodeFrom((await refetch()).data);
      return {
        code:
          refreshed && isCurrent(refreshed.expiresAt)
            ? refreshed.code
            : undefined,
      };
    } catch {
      return { code: undefined };
    }
  }, [held, refetch]);

  if (loading) {
    return { supportCode: { status: 'refreshing' }, getCurrentCode };
  }

  if (held && isCurrent(held.expiresAt)) {
    return {
      supportCode: { status: 'current', code: held.code },
      getCurrentCode,
    };
  }

  return { supportCode: { status: 'expired' }, getCurrentCode };
};
